import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import { useNavigate } from "react-router-dom";

import { useGetCommercialProposalQuery } from "../../store/apis/commercialProposalApi";
import {
  ProposalBoardCard,
  useGetPursuitSheetQuery,
} from "../../store/apis/commercialDashboardApi";
import { QualityChip } from "./DashboardBits";
import {
  amountText,
  BUSINESS_LINE_LABEL,
  ENGAGEMENT_LABEL,
  HOUSE_NAME,
  label,
  LOST_REASON_LABEL,
  ORIGIN_LABEL,
  PROPOSAL_STATUS_LABEL,
  shortDate,
  stageLabel,
} from "./dashboardFormat";

/** Lo que el dashboard pide abrir en el panel lateral. */
export type SheetTarget =
  | { type: "proposal"; id: string; card?: ProposalBoardCard; note?: string }
  | { type: "pursuit"; id: string };

const Field: React.FC<{ name: string; value: React.ReactNode }> = ({
  name,
  value,
}) => (
  <Stack direction="row" justifyContent="space-between" gap={2} py={0.5}>
    <Typography variant="body2" color="text.secondary">
      {name}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: "right" }}>
      {value}
    </Typography>
  </Stack>
);

/** Un paso del recorrido, con su fecha o «pendiente». */
const Step: React.FC<{ name: string; date: string | null; done: boolean }> = ({
  name,
  date,
  done,
}) => (
  <Stack direction="row" gap={1} alignItems="center" py={0.25}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        flexShrink: 0,
        border: "2px solid",
        borderColor: done ? "primary.main" : "grey.400",
        backgroundColor: done ? "primary.main" : "transparent",
      }}
    />
    <Typography variant="body2" sx={{ flexGrow: 1 }}>
      {name}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {date ? shortDate(date) : done ? "" : "pendiente"}
    </Typography>
  </Stack>
);

const ProposalSheet: React.FC<{
  id: string;
  card?: ProposalBoardCard;
  note?: string;
}> = ({ id, card, note }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetCommercialProposalQuery(id);
  const proposal = data?.data;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !proposal) {
    return <Alert severity="error">No se pudo cargar la propuesta.</Alert>;
  }

  const houses = [
    ...new Set(
      proposal.projects
        .map((project) => project.house)
        .filter((house): house is NonNullable<typeof house> => Boolean(house))
    ),
  ];
  const decided =
    proposal.status === "ACEPTADA" || proposal.status === "RECHAZADA";
  const toIso = (value: string | Date | null | undefined) =>
    value ? String(value).slice(0, 10) : null;

  return (
    <Stack gap={2}>
      <Box>
        <Typography
          variant="caption"
          sx={{ fontFamily: "monospace", fontWeight: 700 }}
        >
          {proposal.proposalNumber}
        </Typography>
        <Typography variant="h6">
          {proposal.brandName ?? proposal.clientName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {proposal.name}
        </Typography>
        <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={label(PROPOSAL_STATUS_LABEL, proposal.status)}
          />
          <Chip
            size="small"
            variant="outlined"
            label={
              proposal.clientKind === "PROSPECT"
                ? "New Business"
                : "Existing Business"
            }
          />
          <Chip
            size="small"
            variant="outlined"
            label={label(ENGAGEMENT_LABEL, proposal.clientEngagement)}
          />
          {houses.map((house) => (
            <Chip
              key={house}
              size="small"
              variant="outlined"
              label={label(HOUSE_NAME, house)}
            />
          ))}
          <QualityChip />
        </Stack>
      </Box>

      {note && <Alert severity="warning">{note}</Alert>}

      <Box>
        <Typography variant="subtitle2">Datos económicos</Typography>
        <Field
          name="Presupuesto orientativo"
          value={amountText(proposal.totalBudget || null)}
        />
        <Typography variant="caption" color="text.secondary">
          La inversión gestionada, la facturada, el fee y el beneficio bruto
          llegarán con la valoración de la Proposal Factory.
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Field
          name="Responsable comercial"
          value={proposal.responsableComercialName ?? "—"}
        />
        <Field
          name="Responsable de la propuesta"
          value={proposal.responsablePropuestaName ?? "—"}
        />
        {card && (
          <>
            <Field name="Origen" value={label(ORIGIN_LABEL, card.origin)} />
            <Field
              name="Probabilidad (Twenty)"
              value={card.probability === null ? "—" : `${card.probability} %`}
            />
            <Field
              name="Próximo paso"
              value={
                card.nextStep
                  ? `${card.nextStep}${card.nextStepDate ? ` · ${shortDate(card.nextStepDate)}` : ""}`
                  : "Sin próximo paso"
              }
            />
            {card.lostReason && (
              <Field
                name="Motivo de pérdida"
                value={label(LOST_REASON_LABEL, card.lostReason)}
              />
            )}
          </>
        )}
        <Field
          name="Ejecución estimada"
          value={`${shortDate(toIso(proposal.estimatedActivationDate))} · ${proposal.estimatedDurationMonths} ${
            proposal.estimatedDurationMonths === 1 ? "mes" : "meses"
          }`}
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" mb={0.5}>
          Recorrido
        </Typography>
        <Step name="Número creado" date={toIso(proposal.entryDate)} done />
        <Step
          name="En preparación"
          date={toIso(proposal.startedAt)}
          done={proposal.status !== "BACKLOG"}
        />
        <Step
          name={`Entregada (comprometida el ${shortDate(toIso(proposal.deliveryDeadline))})`}
          date={toIso(proposal.deliveredAt)}
          done={Boolean(proposal.deliveredAt) || decided}
        />
        <Step
          name="Respuesta del cliente"
          date={toIso(proposal.decidedAt)}
          done={decided}
        />
      </Box>

      <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/commercial-proposals/${proposal.id}`)}
        >
          Abrir la ficha completa
        </Button>
        {proposal.crmUrl && (
          <Button
            variant="outlined"
            size="small"
            endIcon={<LaunchIcon />}
            href={proposal.crmUrl}
            target="_blank"
            rel="noreferrer"
          >
            Abrir en Twenty
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

/** Desde Brief ya se puede pedir número de propuesta. */
const CAN_REQUEST_PROPOSAL = new Set(["BRIEF", "PROPUESTA", "NEGOCIACION"]);

const PursuitSheetView: React.FC<{
  id: string;
  onOpenProposal: (id: string) => void;
}> = ({ id, onOpenProposal }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetPursuitSheetQuery(id);
  const sheet = data?.data;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !sheet) {
    return <Alert severity="error">No se pudo cargar la oportunidad.</Alert>;
  }

  const { card } = sheet;
  return (
    <Stack gap={2}>
      <Box>
        <Typography variant="h6">{card.name ?? "Sin nombre"}</Typography>
        <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={stageLabel(card.stage)} />
          <Chip
            size="small"
            variant="outlined"
            label={BUSINESS_LINE_LABEL.NB}
          />
          {card.engagement && (
            <Chip
              size="small"
              variant="outlined"
              label={label(ENGAGEMENT_LABEL, card.engagement)}
            />
          )}
          <QualityChip />
        </Stack>
      </Box>

      <Box>
        <Field name="Potencial" value={amountText(card.amount)} />
        <Field name="Owner" value={card.ownerName ?? "—"} />
        <Field name="Origen" value={label(ORIGIN_LABEL, card.sourceType)} />
        <Field
          name="Días en fase"
          value={card.daysInStage === null ? "—" : String(card.daysInStage)}
        />
        <Field
          name="Próximo paso"
          value={
            card.nextAction
              ? `${card.nextAction}${card.nextActionDate ? ` · ${shortDate(card.nextActionDate)}` : ""}`
              : "Sin próximo paso"
          }
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" mb={0.5}>
          Recorrido por fases
        </Typography>
        {sheet.history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin cambios de fase registrados todavía: el historial empieza con la
            copia local de Twenty.
          </Typography>
        ) : (
          sheet.history.map((event, index) => (
            <Step
              key={`${event.changedAt}-${index}`}
              name={
                event.toStage
                  ? `${event.fromStage ? `${stageLabel(event.fromStage)} → ` : ""}${stageLabel(event.toStage)}`
                  : "Ya no está en Twenty"
              }
              date={String(event.changedAt).slice(0, 10)}
              done
            />
          ))
        )}
      </Box>

      {sheet.proposals.length > 0 && (
        <Box>
          <Typography variant="subtitle2" mb={0.5}>
            Propuestas
          </Typography>
          {sheet.proposals.map((proposal) => (
            <Stack
              key={proposal.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              py={0.5}
            >
              <Button
                size="small"
                onClick={() => onOpenProposal(proposal.id)}
                sx={{ fontFamily: "monospace", textTransform: "none", p: 0 }}
              >
                {proposal.proposalNumber}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {label(PROPOSAL_STATUS_LABEL, proposal.status)} ·{" "}
                {amountText(proposal.amount || null)}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}

      <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
        {card.crmUrl && (
          <Button
            variant="outlined"
            size="small"
            endIcon={<LaunchIcon />}
            href={card.crmUrl}
            target="_blank"
            rel="noreferrer"
          >
            Abrir en Twenty
          </Button>
        )}
        {CAN_REQUEST_PROPOSAL.has(card.stage) && (
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/commercial-proposals/new")}
          >
            Pedir número de propuesta
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

interface Props {
  target: SheetTarget | null;
  onClose: () => void;
  onOpen: (target: SheetTarget) => void;
}

/**
 * El panel lateral con la ficha. Se cierra con la ×, pulsando fuera o con Escape (el
 * Drawer de MUI ya hace las dos últimas).
 */
const DashboardSheets: React.FC<Props> = ({ target, onClose, onOpen }) => (
  <Drawer
    anchor="right"
    open={Boolean(target)}
    onClose={onClose}
    PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, p: 2.5 } }}
  >
    <Stack direction="row" justifyContent="flex-end">
      <IconButton aria-label="Cerrar la ficha" onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </Stack>
    {target?.type === "proposal" && (
      <ProposalSheet
        key={target.id}
        id={target.id}
        card={target.card}
        note={target.note}
      />
    )}
    {target?.type === "pursuit" && (
      <PursuitSheetView
        key={target.id}
        id={target.id}
        onOpenProposal={(id) => onOpen({ type: "proposal", id })}
      />
    )}
  </Drawer>
);

export default DashboardSheets;
